'use client';

import type { CSSProperties } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { useUiContent } from '@/components/ui-content-provider';
import { useChangePulse } from '@/components/products/live-products-provider';
import { replacePlaceholders } from '@/lib/ui-content/price-ui-content';
import AmazonIcon from '@/public/icons/amazon-icon.svg';
import { AffiliateLinkTracker } from '../affiliate-link-tracker';
import { RetailerFavicon } from '../retailer-favicon';
import { SUBLINE_SEPARATOR, offerRowCopy } from './offer-copy';
import { useViewerClock } from '@/lib/hooks/use-viewer-clock';
import { brandHue, isAmazonOffer, type PreparedOffer } from './offers';

type PickLineProps = {
  /** `prepareOffers().primary` — the recommended option, and the ONLY offer
   *  this component ever renders. It is excluded from the row list. */
  offer: PreparedOffer;
  /** Its position in the FULL prepared list, so `offer_index` stays comparable
   *  with the rows around it. */
  offerIndex: number;
  localeDetails: LocaleDetails;
  productTitle: string;
  index: number;
  categoryId?: string;
  asin?: string;
  href: string;
  /** Composed by external-link.tsx: the link's accessible name, the same one
   *  the legacy pill uses. No hidden paragraph rides inside the link. */
  ctaAriaLabel?: string;
  onClick?: () => void;
  /** False while the panel has never been opened — see `RetailerFavicon.active`.
   *  Only meaningful for `'morph'`; the sheet mounts on open. */
  active?: boolean;
  /**
   * `'morph'` renders BOTH layouts and lets the CSS cross-fade them: compact
   * (the pill: centred "Buy Now" over the retailer's mark) while closed, full
   * (a row: mark, "Buy Now", retailer and badge, price, arrow) while open.
   *
   * `'sheet'` renders the full layout only, statically. The bottom sheet has no
   * closed state to morph from — the pill it came from is still on the card
   * behind the scrim.
   */
  variant: 'morph' | 'sheet';
};

/**
 * The recommended offer, rendered as the buy button.
 *
 * ONE anchor, two layouts. This is the element that used to be the pill's left
 * zone, and on desktop it is now also the bottom line of the open panel: it
 * does not fade out and it does not move, it widens in place (see
 * `.offers-morph-face-pill` in globals.css and rule 6 in use-offers-morph.ts).
 * Because it is one anchor throughout, the href, the `/go` hop and the tracking
 * props are the same open and closed — there is no second CTA that could drift
 * from this one. The page-level layers (`itemProp="url"`,
 * `data-purpose="purchase-cta"`, the sr-only disclosure) ride on the `'morph'`
 * form only, because a page must carry exactly one of each and the card's pill
 * is still in the DOM while the mobile sheet is open.
 *
 * INVERTED SURFACE. The line is `bg-ink`, which is near-black in light mode and
 * near-white in dark. So the text is `text-light dark:text-black` and every
 * tint inside is `white/x dark:black/x` — the mirror of the panel around it,
 * and the reason the marks get their own tile and monogram rules.
 *
 * The open layout is deliberately the same skeleton as `offer-row.tsx`: mark,
 * name, badge, price, an arrow pointing right. It is one of the options, and it
 * is the one we recommend; it should not also look like a different kind of
 * control.
 */
export function PickLine({
  offer,
  offerIndex,
  localeDetails,
  productTitle,
  index,
  categoryId,
  asin,
  href,
  ctaAriaLabel,
  onClick,
  active,
  variant,
}: PickLineProps) {
  const ui = useUiContent();
  const isEnglish = localeDetails.languageCode === 'en';
  const isAmazon = isAmazonOffer(offer);
  const name = offer.merchantName?.trim() || offer.domainKey;
  // A SEARCH row (the Amazon fallback for a product whose stores are all out
  // of stock — see preferAmazonWhenOutOfStock) says what it is. Both keys are
  // REQUIRED and hand-set in all 24 core languages — never fall back.
  const label = offer.kind === 'search' ? ui.searchNow : ui.buyNow;
  const pulsing = useChangePulse(`${offer.domainKey}|${offer.priceLabel}`);

  // The same sentences the row above it would carry (offer-copy.ts): the
  // badge chip, the "Brand store" and "Deal" chips, the "as of" stamp and the
  // other notes on the second line, the honesty chip under the price. The
  // CLOSED pill never shows a stamp or a chip (spec 6.1.5): only the full
  // layout below reads these.
  const viewerLocal = useViewerClock();
  const copy = offerRowCopy(offer, ui, localeDetails, Date.now(), viewerLocal);
  const badgeLabel = copy.badgeLabel;
  const approxSentence = copy.approxSentence;
  // The price slot says what the row above it would: the price ("~" when
  // hedged), or a label ("MSRP {price}", "See price at {merchant}") that must
  // never be printed as if it were the price.
  const priceText = copy.priceSlot;
  const secondLine = copy.notes ? `${name}${SUBLINE_SEPARATOR}${copy.notes}` : name;

  // The retailer's square mark, same source chain and same monogram fallback as
  // every row above it. `.offers-pick-ink` is what re-tints the monogram for
  // this inverted surface (globals.css).
  const mark = (
    <span className="grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-md bg-white/90 dark:bg-black/10">
      <RetailerFavicon
        domain={offer.domainKey}
        displayName={name}
        active={variant === 'sheet' ? true : active}
        className="h-[1.05rem] w-[1.05rem]"
        fallback={
          <span
            aria-hidden
            style={{ '--brand-h': brandHue(offer.domainKey) } as CSSProperties}
            className="retailer-monogram inline-flex h-[1.05rem] w-[1.05rem] shrink-0 items-center justify-center text-[0.68rem] font-semibold leading-none"
          >
            {(name.charAt(0) || '?').toUpperCase()}
          </span>
        }
      />
    </span>
  );

  // The open / sheet layout. Same skeleton as OfferRow, inverted palette, and
  // "Buy Now" where a row would put the retailer's name — the retailer drops to
  // the second line beside its badge, which is the hierarchy the pill already
  // had and the reason this reads as the button it is.
  const fullBody = (
    <>
      {mark}
      <span className="flex min-w-0 flex-1 flex-col justify-center">
        {/* REQUIRED key, hand-set in all 24 core languages — an `??` fallback
            here would silently downgrade 20 locales. */}
        <span className="truncate text-[0.95rem] font-semibold leading-tight">{label}</span>
        <span className="flex min-w-0 items-center gap-1.5">
          {/* Its text can gain the "as of" stamp between the cached server
              render and hydration; only the text, never the element. */}
          <span suppressHydrationWarning className="truncate text-[0.66rem] leading-tight opacity-75">
            {secondLine}
          </span>
          {badgeLabel && (
            <span className="shrink-0 whitespace-nowrap rounded-full bg-white/20 px-1.5 py-[0.1rem] text-[0.6rem] font-semibold leading-[1.15] dark:bg-black/10">
              {badgeLabel}
            </span>
          )}
          {copy.brandStoreLabel && (
            <span className="shrink-0 whitespace-nowrap rounded-full bg-white/20 px-1.5 py-[0.1rem] text-[0.6rem] font-semibold leading-[1.15] dark:bg-black/10">
              {copy.brandStoreLabel}
            </span>
          )}
          {/* Present whenever the pick has a deal; only the text follows the
              clock, and an empty chip hides itself (OfferRowCopy.hasDealChip). */}
          {copy.hasDealChip && (
            <span
              suppressHydrationWarning
              className="shrink-0 whitespace-nowrap rounded-full bg-white/20 px-1.5 py-[0.1rem] text-[0.6rem] font-semibold leading-[1.15] empty:hidden dark:bg-black/10"
            >
              {copy.dealLabel}
            </span>
          )}
        </span>
      </span>
      {priceText && (
        <span className="flex shrink-0 flex-col items-end justify-center">
          <span
            className={`${
              copy.priceSlotIsPrice
                ? 'tabular-nums text-[0.95rem] font-semibold'
                : 'max-w-[9rem] truncate text-right text-[0.72rem] font-medium'
            } leading-tight transition-opacity duration-150 ${pulsing ? 'opacity-70' : 'opacity-100'}`}
          >
            {priceText}
          </span>
          {offer.originalPriceLabel && (
            <span className="tabular-nums text-[0.66rem] leading-tight line-through opacity-60">
              {offer.originalPriceLabel}
            </span>
          )}
          {copy.belowUsual && (
            <span
              title={copy.belowUsual.tooltip}
              className="whitespace-nowrap text-[0.58rem] font-semibold leading-tight opacity-80"
            >
              {copy.belowUsual.label}
            </span>
          )}
        </span>
      )}
      {/* Points RIGHT, like every option above it: this line goes to the
          retailer. The chevron that used to sit at the far end pointed up and
          collapsed the panel, which made the recommended option the one row
          that behaved differently from all the others. Closing is the header's
          Back button, Escape, an outside click or simply moving the pointer
          away. */}
      <svg
        aria-hidden
        viewBox="0 0 24 24"
        className="h-3.5 w-3.5 shrink-0 opacity-50 transition-transform duration-200 group-hover:translate-x-0.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m9 6 6 6-6 6" />
      </svg>
    </>
  );

  const tracker = {
    href,
    productTitle,
    isAmazon,
    locale: localeDetails.languageCode,
    region: localeDetails.region,
    index,
    asin: isAmazon ? asin : undefined,
    categoryId,
    onClick,
    ariaLabel: ctaAriaLabel,
    title: ctaAriaLabel,
    // EXACTLY ONE of these per page. The microdata `itemProp="url"` and the
    // `data-purpose` annotation belong to the card's own CTA, which is this
    // component in `'morph'` form and is still in the DOM behind the sheet's
    // scrim on mobile. Emitting them again from the sheet would put two
    // `url` properties in one Offer scope and two purchase CTAs in one page.
    ...(variant === 'morph' ? { itemProp: 'url', dataPurpose: 'purchase-cta' } : {}),
    extraTrackProps: {
      merchant_domain: offer.domainKey,
      offer_index: offerIndex,
      offer_kind: 'primary',
    },
  } as const;

  if (variant === 'sheet') {
    return (
      <AffiliateLinkTracker
        {...tracker}
        className="offers-pick-ink group flex w-full cursor-pointer items-center gap-2.5 rounded-xl bg-ink px-2 py-2.5 text-light transition-colors hover:bg-ink/90 dark:text-black"
      >
        {fullBody}
        {copy.belowUsual && <span className="sr-only">{copy.belowUsual.tooltip}</span>}
        {approxSentence && <span className="sr-only">{approxSentence}</span>}
      </AffiliateLinkTracker>
    );
  }

  return (
    <AffiliateLinkTracker
      {...tracker}
      // `relative` so the absolute full layout resolves against this anchor.
      //
      // Deliberately NOT `flex-1 min-w-0` here. Closed, this is a flex item in
      // an auto-width pill, and `flex: 1 1 0%` makes its max-content
      // contribution its zero basis while `min-width: 0` removes the automatic
      // minimum that would otherwise protect the label — so the pill sizes as
      // if "Buy Now" were not there and the text slides under the divider. The
      // grow it needs while OPEN is applied by `.offers-morph.is-open
      // .offers-pick-ink` (globals.css), where the pill has an explicit width
      // and there is genuinely space to take.
      // NO hover background. Hovering this pill already does something large —
      // it opens the whole menu — so a wash over the ink on top of that is a
      // second signal for one intent, and it lights up during the grow while
      // the surface underneath it is still moving. The hover affordances that
      // survive are the ones that say something the open state does not: the
      // arrow's nudge here, the label brightening on the stores toggle.
      className="offers-pick-ink group relative flex cursor-pointer items-center"
    >
      {/* Compact: IN FLOW, because this is the box `useOffersMorph` measures the
          pill from on the open click. Making it absolute would collapse the
          pill to zero and pin the CTA as a permanent sliver.
          Extra sm: width (pl-7 / text-lg) is English-only. The buy column is
          `sm:w-72 md:w-80`, and "Acheter maintenant" at the bumped size plus
          the stores toggle overshoots it; height (`sm:py-2`) is free. */}
      <span
        className={`offers-pick-compact gap-2 py-1.5 pl-6 pr-4 sm:py-2 ${
          isEnglish ? 'sm:gap-2.5 sm:pl-7 sm:pr-5' : ''
        }`}
      >
        <span className="flex flex-col items-center">
          <span
            className={`whitespace-nowrap font-medium leading-tight ${
              isEnglish ? 'text-base sm:text-lg' : 'text-sm'
            }`}
          >
            {label}
          </span>
          <span className="flex h-[1.15rem] items-center gap-1 sm:h-5">
            {isAmazon ? (
              // The bundled WORDMARK, not the square favicon: there is room for
              // it here and nowhere else, and `fill-current` carries it across
              // the inverted surface in both themes.
              <AmazonIcon className="h-[0.95rem] w-auto fill-current sm:h-[1.1rem]" />
            ) : (
              <>
                <RetailerFavicon
                  domain={offer.domainKey}
                  displayName={name}
                  className="h-[0.9rem] w-[0.9rem] sm:h-4 sm:w-4"
                  // The default fallback is `bg-black/10 text-black/75`, which
                  // is tuned for the panel's light surface and disappears on
                  // ink. This is the same monogram, inverted.
                  fallback={
                    <span
                      aria-hidden
                      className="inline-flex h-[0.9rem] w-[0.9rem] shrink-0 items-center justify-center rounded-sm bg-white/20 text-[0.6rem] font-semibold leading-none text-light dark:bg-black/10 dark:text-black sm:h-4 sm:w-4 sm:text-[0.65rem]"
                    >
                      {(name.charAt(0) || '?').toUpperCase()}
                    </span>
                  }
                />
                <span className="max-w-[6.5rem] truncate text-[0.68rem] font-medium leading-none opacity-75 sm:max-w-[8rem] sm:text-xs">
                  {name}
                </span>
              </>
            )}
          </span>
        </span>
      </span>

      <span className="offers-pick-full gap-2.5 pl-2 pr-2.5">{fullBody}</span>

      {copy.belowUsual && <span className="sr-only">{copy.belowUsual.tooltip}</span>}
      {approxSentence && <span className="sr-only">{approxSentence}</span>}
    </AffiliateLinkTracker>
  );
}
